import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Logo } from '../components/Logo';
import { 
  CheckCircle, 
  Shield, 
  Clock, 
  ArrowRight, 
  Brain, 
  Heart, 
  Zap, 
  UserCheck,
  Calendar,
  FileText,
  Award,
  Stethoscope,
  Video,
  MessageCircle,
  MapPin
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [popularTests, setPopularTests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTests = async () => {
      try {
        const response = await axios.get(`${API}/tests?language=${language}`);
        setPopularTests(response.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching tests:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTests();
  }, [language]);

  const features = [
    {
      icon: Award,
      title: language === 'it' ? 'Test Validati Scientificamente' : 'Scientifically Validated Tests',
      description: language === 'it' 
        ? 'Strumenti di assessment riconosciuti dalla comunità scientifica internazionale'
        : 'Assessment tools recognized by the international scientific community',
    },
    {
      icon: Shield,
      title: language === 'it' ? 'Privacy Garantita' : 'Guaranteed Privacy',
      description: language === 'it'
        ? 'I tuoi dati sono protetti e trattati in conformità GDPR'
        : 'Your data is protected and processed in compliance with GDPR',
    },
    {
      icon: UserCheck,
      title: language === 'it' ? 'Supporto Professionale' : 'Professional Support',
      description: language === 'it'
        ? 'Consulenza con psicoterapeuti qualificati per interpretare i risultati'
        : 'Consultation with qualified psychotherapists to interpret results',
    },
  ];

  const categoryIcons = {
    depression: Brain,
    anxiety: Heart,
    stress: Zap,
    comprehensive: Shield,
    wellbeing: Heart,
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Studio Sul Web Section - FIRST */}
      <section className="pt-24 pb-16 md:pt-28 md:pb-24 bg-gradient-to-br from-primary via-primary to-[#1e3a5f] relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-accent/20 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-white/5 to-transparent rounded-full blur-3xl" />
        
        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            {/* Badge */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-8"
            >
              <MapPin className="w-4 h-4 text-[#60A5FA]" />
              <span className="text-sm font-semibold text-white">
                {language === 'it' ? 'Roma • Da oltre 15 anni' : 'Rome • Over 15 years'}
              </span>
            </motion.div>

            {/* Main Title */}
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white mb-4 leading-tight"
            >
              {language === 'it' 
                ? <><span className="font-normal">Lo Studio Professionale</span><br /><span className="font-bold text-white">Il </span><span className="font-bold text-[#60A5FA]">Mio </span><span className="font-bold text-white">Terapeuta</span><br /><span className="font-normal">si sposta anche sul </span><span className="relative inline-block font-normal">web<span className="absolute -right-6 -top-2 text-[10px] font-bold text-white bg-[#60A5FA] px-1.5 py-0.5 rounded-md" style={{ transform: 'rotate(-12deg)' }}>WEB</span></span></>
                : <><span className="font-normal">The Professional Studio</span><br /><span className="font-bold text-white">Il </span><span className="font-bold text-[#60A5FA]">Mio </span><span className="font-bold text-white">Terapeuta</span><br /><span className="font-normal">goes </span><span className="relative inline-block font-normal">online<span className="absolute -right-6 -top-2 text-[10px] font-bold text-white bg-[#60A5FA] px-1.5 py-0.5 rounded-md" style={{ transform: 'rotate(-12deg)' }}>WEB</span></span></>}
            </motion.h1>

            {/* Subtitle */}
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.6 }}
              className="text-lg sm:text-xl text-[#60A5FA] font-medium mb-10 max-w-2xl mx-auto"
            >
              {language === 'it'
                ? 'Prendersi cura della propria mente è una scelta importante: affidati a professionisti qualificati, con esperienza reale sul territorio.'
                : 'Taking care of your mind is an important choice: trust qualified professionals with real experience in the field.'}
            </motion.p>

            {/* Powerful Quote */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.45, duration: 0.6 }}
              className="mb-10 px-6 py-6 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 max-w-3xl mx-auto"
            >
              <p className="text-xl sm:text-2xl font-serif text-white leading-relaxed mb-3">
                {language === 'it'
                  ? '"Non tutte le terapie sono uguali: la differenza la fanno esperienza, metodo e presenza reale."'
                  : '"Not all therapies are equal: the difference lies in experience, method and real presence."'}
              </p>
              <p className="text-lg sm:text-xl font-bold text-[#60A5FA]">
                {language === 'it'
                  ? 'La tua mente non è un esperimento. Scegli chi la cura da anni.'
                  : 'Your mind is not an experiment. Choose those who have been caring for it for years.'}
              </p>
            </motion.div>

            {/* Features Grid */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6"
            >
              {[
                { icon: Award, text: language === 'it' ? '15+ Anni di Esperienza' : '15+ Years Experience' },
                { icon: UserCheck, text: language === 'it' ? 'Specialisti Certificati' : 'Certified Specialists' },
                { icon: Brain, text: 'Evidence-Based' },
                { icon: Zap, text: language === 'it' ? 'Risultati Rapidi' : 'Quick Results' }
              ].map((item, idx) => (
                <motion.div 
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + idx * 0.1, duration: 0.4 }}
                  className="flex flex-col items-center gap-3 p-4 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-colors"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#60A5FA]/20 flex items-center justify-center">
                    <item.icon className="w-6 h-6 text-[#60A5FA]" />
                  </div>
                  <span className="text-sm font-medium text-white text-center">{item.text}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Hero Section - Valutazione Psicologica */}
      <section className="relative py-20 md:py-28 overflow-hidden hero-pattern">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <div className="absolute top-20 right-[10%] w-[500px] h-[500px] bg-gradient-to-br from-accent/20 to-accent/5 rounded-full blur-3xl animate-float" />
          <div className="absolute -bottom-20 -left-20 w-[600px] h-[600px] bg-gradient-to-tr from-primary/10 to-primary/5 rounded-full blur-3xl" style={{ animationDelay: '2s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-accent/5 to-transparent rounded-full" />
        </div>

        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-4xl mx-auto text-center"
          >
            {/* Premium Badge */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full badge-gold mb-8"
            >
              <Shield className="w-4 h-4 text-accent" />
              <span className="text-sm font-semibold text-primary">
                {language === 'it' ? 'Piattaforma Certificata' : 'Certified Platform'}
              </span>
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-serif font-normal text-primary mb-6 leading-[1.1]" 
              data-testid="hero-title"
            >
              {language === 'it' 
                ? <>Valutazione<br /><span className="gold-text font-bold">Psicologica</span><br />Specialistica</>
                : <>Psychological<br /><span className="gold-text font-bold">Assessment</span><br />Specialized</>}
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="text-lg sm:text-xl text-muted-foreground mb-10 leading-relaxed max-w-2xl mx-auto font-light" 
              data-testid="hero-subtitle"
            >
              {language === 'it'
                ? 'Valutazioni cliniche standardizzate con supporto specialistico. Comprendi il tuo benessere mentale con strumenti validati.'
                : 'Standardized clinical assessments with specialist support. Understand your mental wellbeing with validated tools.'}
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Button
                size="lg"
                className="btn-gold rounded-xl px-10 h-14 text-base font-semibold group"
                onClick={() => navigate('/pricing')}
                data-testid="hero-start-btn"
              >
                {language === 'it' ? 'Inizia Valutazione' : 'Start Assessment'}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              
              <Button
                size="lg"
                variant="outline"
                className="rounded-xl border-2 border-primary/20 hover:border-primary/40 hover:bg-primary/5 px-10 h-14 text-base font-medium backdrop-blur-sm"
                onClick={() => navigate('/tests')}
                data-testid="hero-view-tests-btn"
              >
                {language === 'it' ? 'Scopri i Test' : 'Explore Tests'}
              </Button>
            </motion.div>

            {/* Free Screening Banner */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="mt-8"
            >
              <div 
                onClick={() => navigate('/screening')}
                className="cursor-pointer inline-flex items-center gap-3 px-6 py-4 rounded-2xl transition-all shadow-lg hover:shadow-xl"
                style={{
                  background: 'linear-gradient(to right, #FCD34D, #FBBF24, #FCD34D)',
                  border: '2px solid #F59E0B'
                }}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#F59E0B' }}>
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <p className="font-bold" style={{ color: '#78350F' }}>
                    {language === 'it' ? 'Fai prima uno screening gratuito' : 'Take a free screening first'}
                  </p>
                  <p className="text-sm" style={{ color: '#92400E' }}>
                    {language === 'it' ? 'Valuta il tuo stato psicologico in soli 3 minuti' : 'Assess your psychological state in just 3 minutes'}
                  </p>
                </div>
                <ArrowRight className="w-5 h-5" style={{ color: '#B45309' }} />
              </div>
            </motion.div>

            {/* Trust indicators */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="flex flex-wrap justify-center gap-6 md:gap-10 mt-14 pt-10 border-t border-primary/10"
            >
              {[
                { icon: CheckCircle, text: language === 'it' ? 'Test Validati' : 'Validated Tests' },
                { icon: Shield, text: 'GDPR Compliant' },
                { icon: UserCheck, text: language === 'it' ? 'Supporto Specialistico' : 'Specialist Support' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="w-8 h-8 rounded-lg icon-container-gold flex items-center justify-center">
                    <item.icon className="w-4 h-4 text-accent" />
                  </div>
                  <span className="font-medium">{item.text}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
        
        {/* Decorative bottom wave */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent" />
      </section>

      {/* Il Mio Terapeuta Card Section */}
      <section className="py-16 md:py-20 bg-white relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-accent/5 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-primary/5 to-transparent rounded-full blur-3xl" />
        
        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto"
          >
            <div className="elegant-card rounded-3xl p-8 md:p-12">
              <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
                {/* Logo/Icon with animation */}
                <div className="flex-shrink-0">
                  <motion.div 
                    whileHover={{ scale: 1.05, rotate: 2 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="w-28 h-28 md:w-36 md:h-36 rounded-3xl blue-gradient flex items-center justify-center shadow-xl animate-pulse-glow"
                  >
                    <Award className="w-14 h-14 md:w-18 md:h-18 text-white" />
                  </motion.div>
                </div>
                
                {/* Content */}
                <div className="text-center flex-1">
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full badge-gold text-sm font-semibold mb-4">
                    <MapPin className="w-3.5 h-3.5 text-accent" />
                    <span className="text-primary">Roma, Italia</span>
                  </div>
                  <div className="mb-4 flex justify-center">
                    <Logo size="lg" inline={true} />
                  </div>
                  <p className="text-muted-foreground leading-relaxed mb-6 text-base">
                    {language === 'it'
                      ? 'Studio professionale che opera sul territorio di Roma da oltre 15 anni, con specialisti esperti e qualificati, selezionati dal responsabile dott. Gianmaria Palumbo, psicologo/psicoterapeuta e direttore di molteplici progetti nel mondo della clinica, sport d\'élite e aziendale.'
                      : 'A professional studio operating in Rome for over 15 years, with expert and qualified specialists, selected by director Dr. Gianmaria Palumbo, psychologist/psychotherapist and director of multiple projects in clinical, elite sports and corporate settings.'}
                  </p>
                  <div className="flex flex-wrap justify-center gap-3">
                    <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-secondary/60 text-sm">
                      <Clock className="w-4 h-4 text-accent" />
                      <span className="font-medium text-primary">{language === 'it' ? '15+ anni' : '15+ years'}</span>
                    </div>
                    <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-secondary/60 text-sm">
                      <UserCheck className="w-4 h-4 text-accent" />
                      <span className="font-medium text-primary">{language === 'it' ? 'Team qualificato' : 'Qualified team'}</span>
                    </div>
                    <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-secondary/60 text-sm">
                      <Brain className="w-4 h-4 text-accent" />
                      <span className="font-medium text-primary">CBT</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Consulta uno Specialista Section */}
      <section className="py-16 md:py-24 bg-primary/5">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 text-accent mb-4">
              <Stethoscope className="w-4 h-4" />
              <span className="text-sm font-semibold">
                {language === 'it' ? 'Servizio Premium' : 'Premium Service'}
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-primary mb-4" data-testid="specialist-title">
              {language === 'it' ? 'Consulta uno Specialista' : 'Consult a Specialist'}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {language === 'it'
                ? 'La nostra Valutazione Completa include due sessioni con un professionista qualificato'
                : 'Our Complete Assessment package includes two sessions with a qualified professional'}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 max-w-6xl mx-auto">
            {/* Step 1 - Registrati */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card className="h-full bg-white border-0 shadow-soft hover:shadow-medium transition-all duration-300 rounded-2xl text-center" data-testid="specialist-step-1">
                <CardHeader className="pb-4">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                    <UserCheck className="w-7 h-7 text-primary" />
                  </div>
                  <div className="w-8 h-8 rounded-full gold-gradient flex items-center justify-center mx-auto mb-2 text-white font-bold text-sm">
                    1
                  </div>
                  <CardTitle className="text-lg font-serif text-primary">
                    {language === 'it' ? 'Registrati' : 'Register'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-muted-foreground leading-relaxed text-sm">
                    {language === 'it'
                      ? 'Crea il tuo account inserendo i tuoi dati anagrafici e procedi al pagamento.'
                      : 'Create your account with your personal details and proceed to payment.'}
                  </CardDescription>
                </CardContent>
              </Card>
            </motion.div>

            {/* Step 2 - Primo Colloquio */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card className="h-full bg-white border-0 shadow-soft hover:shadow-medium transition-all duration-300 rounded-2xl text-center" data-testid="specialist-step-2">
                <CardHeader className="pb-4">
                  <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-3">
                    <Video className="w-7 h-7 text-accent" />
                  </div>
                  <div className="w-8 h-8 rounded-full gold-gradient flex items-center justify-center mx-auto mb-2 text-white font-bold text-sm">
                    2
                  </div>
                  <CardTitle className="text-lg font-serif text-primary">
                    {language === 'it' ? 'Primo Colloquio' : 'First Consultation'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-muted-foreground leading-relaxed text-sm">
                    {language === 'it'
                      ? '30 minuti con lo specialista per definire obiettivi e selezionare i test appropriati.'
                      : '30 minutes with specialist to define goals and select appropriate tests.'}
                  </CardDescription>
                </CardContent>
              </Card>
            </motion.div>

            {/* Step 3 - Compila i Test */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Card className="h-full bg-white border-0 shadow-soft hover:shadow-medium transition-all duration-300 rounded-2xl text-center" data-testid="specialist-step-3">
                <CardHeader className="pb-4">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                    <FileText className="w-7 h-7 text-primary" />
                  </div>
                  <div className="w-8 h-8 rounded-full gold-gradient flex items-center justify-center mx-auto mb-2 text-white font-bold text-sm">
                    3
                  </div>
                  <CardTitle className="text-lg font-serif text-primary">
                    {language === 'it' ? 'Compila i Test' : 'Complete Tests'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-muted-foreground leading-relaxed text-sm">
                    {language === 'it'
                      ? 'Completa i test psicologici in autonomia, quando e dove preferisci.'
                      : 'Complete psychological tests independently, whenever and wherever you prefer.'}
                  </CardDescription>
                </CardContent>
              </Card>
            </motion.div>

            {/* Step 4 - Restituzione */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Card className="h-full bg-white border-0 shadow-soft hover:shadow-medium transition-all duration-300 rounded-2xl text-center" data-testid="specialist-step-4">
                <CardHeader className="pb-4">
                  <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-3">
                    <MessageCircle className="w-7 h-7 text-accent" />
                  </div>
                  <div className="w-8 h-8 rounded-full gold-gradient flex items-center justify-center mx-auto mb-2 text-white font-bold text-sm">
                    4
                  </div>
                  <CardTitle className="text-lg font-serif text-primary">
                    {language === 'it' ? 'Restituzione' : 'Results Review'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-muted-foreground leading-relaxed text-sm">
                    {language === 'it'
                      ? '30 minuti di colloquio finale per discutere i risultati e ricevere indicazioni.'
                      : '30-minute final session to discuss results and receive recommendations.'}
                  </CardDescription>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-center mt-12"
          >
            <Button
              size="lg"
              className="rounded-lg gold-gradient text-white hover:opacity-90 shadow-medium px-10 h-14 text-base font-semibold"
              onClick={() => navigate('/pricing')}
              data-testid="specialist-cta-btn"
            >
              <Calendar className="w-5 h-5 mr-2" />
              {language === 'it' ? 'Prenota la Tua Valutazione — €199' : 'Book Your Assessment — €199'}
            </Button>
            <p className="text-sm text-muted-foreground mt-4">
              {language === 'it' 
                ? 'Include 2 sessioni con specialista + tutti i test' 
                : 'Includes 2 sessions with specialist + all tests'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Assessment Center App Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-blue-50 via-blue-50 to-purple-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-10 left-10 w-72 h-72 bg-blue-200 rounded-full filter blur-3xl" />
          <div className="absolute bottom-10 right-10 w-72 h-72 bg-purple-200 rounded-full filter blur-3xl" />
        </div>
        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600 text-white mb-4">
              <Brain className="w-4 h-4" />
              <span className="text-sm font-semibold">
                {language === 'it' ? 'Centro Assessment Online' : 'Online Assessment Center'}
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-primary mb-4">
              {language === 'it' ? 'Accedi alla Piattaforma di Valutazione' : 'Access the Assessment Platform'}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {language === 'it'
                ? 'Esegui test psicologici standardizzati direttamente online. Ricevi report dettagliati con interpretazione professionale.'
                : 'Take standardized psychological tests directly online. Receive detailed reports with professional interpretation.'}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card className="max-w-4xl mx-auto border-0 shadow-xl rounded-3xl overflow-hidden bg-white">
              <CardContent className="p-0">
                <div className="grid md:grid-cols-2">
                  {/* Left side - Features */}
                  <div className="p-8 md:p-10 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
                    <h3 className="text-xl font-bold mb-6">
                      {language === 'it' ? 'Cosa include:' : 'What includes:'}
                    </h3>
                    <ul className="space-y-4">
                      <li className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 mt-0.5 text-blue-200 flex-shrink-0" />
                        <span>{language === 'it' ? 'Test psicologici validati' : 'Validated psychological tests'}</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 mt-0.5 text-blue-200 flex-shrink-0" />
                        <span>{language === 'it' ? 'Report PDF immediato' : 'Instant PDF report'}</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 mt-0.5 text-blue-200 flex-shrink-0" />
                        <span>{language === 'it' ? 'Interpretazione clinica dettagliata' : 'Detailed clinical interpretation'}</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 mt-0.5 text-blue-200 flex-shrink-0" />
                        <span>{language === 'it' ? 'Storico test e progressi' : 'Test history and progress'}</span>
                      </li>
                    </ul>
                  </div>
                  
                  {/* Right side - CTA */}
                  <div className="p-8 md:p-10 flex flex-col justify-center items-center text-center">
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-100 to-blue-100 flex items-center justify-center mb-6">
                      <FileText className="w-10 h-10 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-primary mb-2">
                      {language === 'it' ? 'Assessment Center' : 'Assessment Center'}
                    </h3>
                    <p className="text-muted-foreground text-sm mb-6">
                      {language === 'it' 
                        ? 'Accedi o registrati per iniziare i test psicologici' 
                        : 'Login or register to start psychological tests'}
                    </p>
                    <Button
                      size="lg"
                      className="w-full rounded-xl bg-blue-600 hover:bg-blue-700 text-white mb-3"
                      onClick={() => navigate('/app/assessment')}
                      data-testid="assessment-center-btn"
                    >
                      <Brain className="w-5 h-5 mr-2" />
                      {language === 'it' ? 'Vai al Centro Test' : 'Go to Test Center'}
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      {language === 'it' ? 'Riservato agli utenti registrati (18+)' : 'Reserved for registered users (18+)'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Treatment Services Section */}
      <section className="py-16 md:py-24 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-accent/3 via-transparent to-primary/3" />
        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-4">
              <Heart className="w-4 h-4" />
              <span className="text-sm font-semibold">
                {language === 'it' ? 'Trattamento Specialistico' : 'Specialized Treatment'}
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-primary mb-4">
              {language === 'it' ? 'Psicoterapia Online e in Presenza' : 'Online and In-Person Psychotherapy'}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {language === 'it'
                ? 'Dopo la diagnosi, inizia un percorso terapeutico personalizzato con obiettivi chiari e misurabili.'
                : 'After diagnosis, start a personalized therapeutic path with clear and measurable goals.'}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-10">
            {/* Terapia Individuale */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card className="h-full bg-gradient-to-br from-blue-50 to-blue-100/50 border-2 border-blue-400 rounded-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-bl-xl">
                  {language === 'it' ? 'INIZIA DA QUI' : 'START HERE'}
                </div>
                <CardHeader className="text-center pb-2 pt-8">
                  <div className="w-16 h-16 rounded-2xl bg-blue-500 flex items-center justify-center mx-auto mb-4 text-white">
                    <MessageCircle className="w-8 h-8" />
                  </div>
                  <CardTitle className="text-xl font-serif text-primary">
                    {language === 'it' ? 'Consulenza Specialistica' : 'Specialist Consultation'}
                  </CardTitle>
                  <div className="flex items-baseline justify-center gap-1 mt-2">
                    <span className="text-3xl font-bold text-blue-600">€4,99</span>
                  </div>
                </CardHeader>
                <CardContent className="pt-2">
                  <p className="text-sm text-muted-foreground text-center mb-4">
                    {language === 'it' 
                      ? 'Hai bisogno di un parere prima di iniziare un percorso? Descrivi la tua situazione e ricevi indicazioni personalizzate basate su evidenze scientifiche.'
                      : 'Need guidance before starting therapy? Describe your situation and receive personalized, evidence-based recommendations.'}
                  </p>
                  <Button
                    className="w-full rounded-xl bg-blue-500 text-white hover:bg-blue-600"
                    onClick={() => navigate('/orientamento')}
                    data-testid="orientation-cta-btn"
                  >
                    {language === 'it' ? 'Richiedi Orientamento' : 'Request Orientation'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Terapia Individuale */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card className="h-full bg-white border-2 border-primary/10 hover:border-accent/30 transition-all duration-300 rounded-2xl">
                <CardHeader className="text-center pb-4">
                  <div className="w-16 h-16 rounded-2xl bg-blue-500 flex items-center justify-center mx-auto mb-4 text-white">
                    <UserCheck className="w-8 h-8" />
                  </div>
                  <CardTitle className="text-xl font-serif text-primary">
                    {language === 'it' ? 'Terapia Individuale' : 'Individual Therapy'}
                  </CardTitle>
                  <div className="flex items-baseline justify-center gap-1 mt-2">
                    <span className="text-3xl font-bold text-primary">€90</span>
                    <span className="text-muted-foreground text-sm">/ {language === 'it' ? 'seduta' : 'session'}</span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-accent flex-shrink-0" />
                      {language === 'it' ? '50 minuti con terapeuta' : '50 minutes with therapist'}
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-accent flex-shrink-0" />
                      {language === 'it' ? 'Online o in presenza' : 'Online or in-person'}
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-accent flex-shrink-0" />
                      {language === 'it' ? 'Piano personalizzato' : 'Personalized plan'}
                    </li>
                  </ul>
                  <Button 
                    onClick={() => navigate('/booking')}
                    className="w-full rounded-xl bg-accent hover:bg-accent/90"
                  >
                    {language === 'it' ? 'Richiedi un Appuntamento' : 'Request an Appointment'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Terapia di Coppia */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Card className="h-full bg-white border-2 border-primary/10 hover:border-accent/30 transition-all duration-300 rounded-2xl">
                <CardHeader className="text-center pb-4">
                  <div className="w-16 h-16 rounded-2xl bg-rose-500 flex items-center justify-center mx-auto mb-4 text-white">
                    <Heart className="w-8 h-8" />
                  </div>
                  <CardTitle className="text-xl font-serif text-primary">
                    {language === 'it' ? 'Terapia di Coppia' : 'Couples Therapy'}
                  </CardTitle>
                  <div className="flex items-baseline justify-center gap-1 mt-2">
                    <span className="text-3xl font-bold text-primary">€110</span>
                    <span className="text-muted-foreground text-sm">/ {language === 'it' ? 'seduta' : 'session'}</span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-accent flex-shrink-0" />
                      {language === 'it' ? '60 minuti di sessione' : '60 minute session'}
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-accent flex-shrink-0" />
                      {language === 'it' ? 'Terapeuta specializzato' : 'Specialized therapist'}
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-accent flex-shrink-0" />
                      {language === 'it' ? 'Sessioni congiunte' : 'Joint sessions'}
                    </li>
                  </ul>
                  <Button 
                    onClick={() => navigate('/booking')}
                    className="w-full rounded-xl bg-accent hover:bg-accent/90"
                  >
                    {language === 'it' ? 'Richiedi un Appuntamento' : 'Request an Appointment'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Button
              variant="outline"
              size="lg"
              className="rounded-xl border-2"
              onClick={() => navigate('/services')}
            >
              {language === 'it' ? 'Vedi Tutti i Servizi' : 'View All Services'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-primary mb-4 flex flex-wrap items-center justify-center gap-2" data-testid="features-title">
              <span>{language === 'it' ? 'Perché Scegliere' : 'Why Choose'}</span>
              <span className="inline-flex items-baseline whitespace-nowrap">
                <span className="font-black text-primary" style={{ fontFamily: 'Arial Black, sans-serif' }}>IL</span>
                <span className="font-black text-[#2563eb] mx-0.5" style={{ fontFamily: 'Arial Black, sans-serif' }}>MIO</span>
                <span className="font-black text-primary" style={{ fontFamily: 'Arial Black, sans-serif' }}>TERAPEUTA</span>
              </span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full bg-white border-0 shadow-soft hover:shadow-medium transition-shadow duration-300 rounded-2xl" data-testid={`feature-card-${index}`}>
                  <CardHeader className="pb-4">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                      <feature.icon className="w-7 h-7 text-primary" />
                    </div>
                    <CardTitle className="text-xl font-serif text-primary">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-muted-foreground leading-relaxed">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-24">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto text-center bg-gradient-to-br from-primary/5 to-accent/5 rounded-3xl p-8 md:p-12"
          >
            <div className="w-16 h-16 rounded-2xl blue-gradient flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-primary mb-4">
              {language === 'it' ? 'Inizia il Tuo Percorso di Benessere' : 'Start Your Wellness Journey'}
            </h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              {language === 'it' 
                ? 'Prenditi cura della tua salute mentale con strumenti professionali e supporto qualificato.'
                : 'Take care of your mental health with professional tools and qualified support.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="rounded-lg gold-gradient text-white hover:opacity-90 shadow-medium px-8 h-14"
                onClick={() => navigate('/pricing')}
                data-testid="final-cta-btn"
              >
                {language === 'it' ? 'Acquista Valutazione Completa' : 'Buy Complete Assessment'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-lg border-2 px-8 h-14"
                onClick={() => navigate('/tests')}
              >
                {language === 'it' ? 'Esplora i Test' : 'Explore Tests'}
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
